import React from 'react';
import { Form, Input, Typography, Select } from 'antd';
// Utils
import { PromoType } from 'helpers/customCampaignConstants';
// Components
import ProductsList from './components/ProductsList/ProductsList';
// Style
import styles from './PaymentSection.module.scss';

const { Text } = Typography;
const { Option } = Select;

type PaymentSectionProps = {
  budget: number | null;
  showReservedBudget?: boolean;
  pricePerAthleteLabel?: string;
  pricePerAthleteLocked?: boolean;
};

const PaymentSection = ({
  budget,
  showReservedBudget = true,
  pricePerAthleteLabel = 'Price Per Talent',
  pricePerAthleteLocked = false,
}: PaymentSectionProps): JSX.Element => {
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Payment</h3>
      <div className={styles.subSection}>
        <div className={styles.row}>
          {showReservedBudget && (
            <Form.Item
              label={
                <Text>
                  Reserved Budget <Text type="secondary">(Paid)</Text>
                </Text>
              }
            >
              <Input
                type="number"
                value={budget ?? 0}
                disabled
                aria-label="Reserved budget"
              />
              <Text type="secondary" className={styles.fieldHint}>
                Reserved budget is funded through launch payment or budget
                top-up and cannot be changed from this edit form.
              </Text>
            </Form.Item>
          )}

          <Form.Item
            name="flatFeePerStore"
            label={<Text>{pricePerAthleteLabel}</Text>}
            extra={
              pricePerAthleteLocked ? (
                <Text type="secondary" className={styles.fieldHint}>
                  The agency has already paid for this offer amount, so it
                  cannot be changed in the current status. It is only editable
                  while the campaign is in draft status.
                </Text>
              ) : undefined
            }
          >
            <Input
              type="number"
              placeholder={`Enter ${pricePerAthleteLabel.toLowerCase()}`}
              aria-label={pricePerAthleteLabel}
              disabled={pricePerAthleteLocked}
            />
          </Form.Item>
        </div>
      </div>

      <ProductsList />

      <div className={styles.row}>
        <Form.Item
          name="promoType"
          label={<Text>Affiliate Type</Text>}
          style={{ maxWidth: 240 }}
        >
          <Select placeholder="Select type">
            <Option value={PromoType.Code}>Promo Code</Option>
            <Option value={PromoType.Link}>Promo Link</Option>
          </Select>
        </Form.Item>

        <Form.Item
          shouldUpdate={(prev, cur) => prev.promoType !== cur.promoType}
          noStyle
        >
          {({ getFieldValue }) => {
            const promoType = getFieldValue('promoType');
            return (
              <Form.Item
                name="promoValue"
                label={
                  <Text>
                    {promoType === PromoType.Link
                      ? 'Promo Link (Optional)'
                      : 'Promo Code (Optional)'}
                  </Text>
                }
              >
                <Input
                  type="text"
                  placeholder={
                    promoType === PromoType.Link
                      ? 'https://brand.com/offer'
                      : 'ADIDAS2026'
                  }
                  aria-label={
                    promoType === PromoType.Link
                      ? 'Promo link for talents to use'
                      : 'Promo code for talents to use'
                  }
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      </div>
    </section>
  );
};

export default PaymentSection;
